import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import {
  X, Check, ArrowRight, Settings, ClipboardList, Shield,
  CheckCircle2, Circle, Loader2, RotateCw,
} from "lucide-react";

const ORANGE = "#F97316";
const TRACK = "#E5E7EB";

const SECTION_ICON = {
  setup: Settings,
  assessment: ClipboardList,
  protection: Shield,
  validation: CheckCircle2,
};

// Ring — SVG circular progress. pct: 0-100, size px, stroke px.
function Ring({ pct, size, stroke, neutral = false, children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = neutral ? c : c - (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={TRACK} strokeWidth={stroke} />
        {!neutral && (
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none" stroke={ORANGE}
            strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={off}
            style={{ transition: "stroke-dashoffset 500ms ease" }}
          />
        )}
      </svg>
      <div style={{
        position: "absolute", inset: 0, display: "flex",
        alignItems: "center", justifyContent: "center", flexDirection: "column",
      }}>
        {children}
      </div>
    </div>
  );
}

// Returns {text, tone:'amber'|'red'} or null for a task's stall.
function stallBadge(stall) {
  if (!stall) return null;
  if (typeof stall.days_idle === "number") {
    if (stall.days_idle > 14) return { text: `Idle ${stall.days_idle} days`, tone: "red" };
    if (stall.days_idle > 7) return { text: `Idle ${stall.days_idle} days`, tone: "amber" };
    return null;
  }
  if (stall.effective_status) {
    if (typeof stall.days_overdue === "number" && stall.days_overdue > 0)
      return { text: `Overdue ${stall.days_overdue} days`, tone: "red" };
    if ((stall.escalation_level || 0) >= 2)
      return { text: "Overdue", tone: "amber" };
  }
  return null;
}

function nextNeedsAttention(task) {
  const s = task?.stall_info;
  if (!s) return false;
  return (typeof s.days_idle === "number" && s.days_idle > 7) || (s.escalation_level || 0) >= 2;
}

const toneStyle = {
  amber: { background: "#FEF3C7", color: "#92400E" },
  red: { background: "#FEE2E2", color: "#991B1B" },
};

const STATUS_ICON = (status) => {
  if (status === "done") return <Check className="w-4 h-4 text-emerald-600" />;
  if (status === "in_progress") return <span className="w-3.5 h-3.5 rounded-full bg-sky-500 inline-block" />;
  return <Circle className="w-4 h-4 text-slate-300" />;
};

export default function GuidedWorkflowPanel() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  const lastClosedRef = useRef(0);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get("/enrichment/my-tasks");
      const d = res.data || {};
      setData(d);
      // default-expand the section of the is_next task
      const nextTask = (d.tasks || []).find((t) => t.is_next);
      const initExpanded = {};
      (d.sections || []).forEach((s) => { initExpanded[s.id] = nextTask ? s.id === nextTask.section : false; });
      setExpanded(initExpanded);
      // one-time stall toast (persist across route remounts within the session)
      let alreadyShown = false;
      try { alreadyShown = sessionStorage.getItem("gwp-stall-toast-shown") === "true"; } catch (_) { alreadyShown = false; }
      if (!alreadyShown) {
        const anyStalled = (d.tasks || []).some((t) => {
          const s = t.stall_info;
          return s && ((typeof s.days_idle === "number" && s.days_idle > 14) || (s.escalation_level || 0) >= 2);
        });
        const allDone = (d.total || 0) > 0 && d.completed === d.total;
        if (anyStalled && !allDone) {
          toast.warning("Some BCM tasks need attention", {
            description: "Check your progress panel for details",
          });
          try { sessionStorage.setItem("gwp-stall-toast-shown", "true"); } catch (_) { /* ignore */ }
        }
      }
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") closePanel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const openPanel = () => {
    setOpen(true);
    if (Date.now() - lastClosedRef.current > 30000) fetchTasks();
  };
  const closePanel = () => { setOpen(false); lastClosedRef.current = Date.now(); };
  const toggle = () => (open ? closePanel() : openPanel());

  const go = (link) => { if (link) navigate(link); closePanel(); };

  // Hide indicator entirely when the user genuinely has no tasks.
  const noTasks = data && !error && (data.total === 0 || !(data.tasks || []).length);
  if (noTasks && !open) return null;

  const pct = data?.progress_pct ?? 0;
  const nextTask = (data?.tasks || []).find((t) => t.is_next);
  const showDot = nextTask && nextNeedsAttention(nextTask);
  const allDone = data && data.total > 0 && data.completed === data.total;

  return (
    <>
      <style>{`
        @keyframes gwp-pulse { 0%{transform:scale(1);opacity:1} 50%{transform:scale(1.5);opacity:.5} 100%{transform:scale(1);opacity:1} }
        .gwp-panel { transition: transform 300ms ease; }
      `}</style>

      {/* Floating circular indicator */}
      {!noTasks && (
        <button
          type="button"
          onClick={toggle}
          data-testid="gwp-indicator"
          aria-label={`Open guided workflow — ${error ? "unavailable" : Math.round(pct) + "% complete"}`}
          style={{
            position: "fixed", bottom: 90, right: 24, zIndex: 40,
            width: 56, height: 56, borderRadius: "50%", background: "#fff",
            border: "1px solid #E5E7EB", boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
            cursor: "pointer", padding: 0, display: "flex",
            alignItems: "center", justifyContent: "center",
          }}
        >
          {loading && !data ? (
            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          ) : (
            <Ring pct={pct} size={52} stroke={5} neutral={error}>
              <span style={{ fontSize: 13, fontWeight: 700, color: error ? "#94A3B8" : "#0F172A" }}>
                {error ? "—" : `${Math.round(pct)}%`}
              </span>
            </Ring>
          )}
          {showDot && !error && (
            <span style={{
              position: "absolute", top: 2, right: 2, width: 12, height: 12,
              borderRadius: "50%", background: ORANGE, border: "2px solid #fff",
            }}>
              <span style={{
                position: "absolute", inset: -2, borderRadius: "50%",
                background: ORANGE, animation: "gwp-pulse 1.6s infinite",
              }} />
            </span>
          )}
        </button>
      )}

      {/* Mobile backdrop */}
      {open && isMobile && (
        <div
          onClick={closePanel}
          data-testid="gwp-backdrop"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 45 }}
        />
      )}

      {/* Slide-out panel */}
      <aside
        className="gwp-panel"
        data-testid="gwp-panel"
        aria-hidden={!open}
        style={{
          position: "fixed", top: 0, right: 0, height: "100vh",
          width: isMobile ? "100vw" : 380, maxWidth: "100vw",
          background: "#F8FAFC", zIndex: 46,
          boxShadow: open ? "-8px 0 24px rgba(0,0,0,0.15)" : "none",
          transform: open ? "translateX(0)" : "translateX(100%)",
          display: "flex", flexDirection: "column", overflowY: "auto",
        }}
      >
        {/* Close */}
        <div style={{ display: "flex", justifyContent: "flex-end", padding: 12 }}>
          <button onClick={closePanel} data-testid="gwp-close" aria-label="Close panel"
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div style={{ padding: "0 20px 32px" }}>
          {error ? (
            <div className="text-center py-16">
              <p className="text-sm font-semibold text-slate-700">Unable to load tasks</p>
              <button onClick={fetchTasks} data-testid="gwp-retry"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm rounded-lg">
                <RotateCw className="w-4 h-4" /> Retry
              </button>
            </div>
          ) : loading && !data ? (
            <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-orange-500 animate-spin" /></div>
          ) : noTasks ? (
            <p className="text-sm text-slate-500 text-center py-16">No action items right now</p>
          ) : (
            <>
              {/* Section A — Header */}
              <div className="text-center">
                <h2 className="text-xl font-bold text-slate-900" data-testid="gwp-title">Your BCM Journey</h2>
                <p className="text-sm text-slate-500 mt-0.5">{data.org_name || "Your Organization"}</p>
                <div className="flex justify-center my-5">
                  <Ring pct={pct} size={112} stroke={9}>
                    <span style={{ fontSize: 26, fontWeight: 800, color: "#0F172A" }}>{Math.round(pct)}%</span>
                  </Ring>
                </div>
                <p className="text-sm font-medium text-slate-600" data-testid="gwp-steps">
                  {data.completed} of {data.total} steps complete
                </p>
              </div>

              {/* Section B — What's Next / All done */}
              {nextTask ? (
                <div data-testid="gwp-next-card"
                  style={{ borderLeft: `4px solid ${ORANGE}` }}
                  className="mt-6 bg-white rounded-lg shadow-sm p-4">
                  <p className="text-[11px] font-bold tracking-wider" style={{ color: ORANGE }}>NEXT STEP</p>
                  <p className="text-sm font-bold text-slate-900 mt-1">{nextTask.title}</p>
                  <p className="text-xs text-slate-500 mt-1">{nextTask.hint}</p>
                  {(() => {
                    const s = nextTask.stall_info;
                    if (!s) return null;
                    if (s.effective_status) {
                      const label = s.effective_status.replace(/_/g, " ");
                      return (
                        <p className="text-xs mt-2" style={{ color: s.days_overdue > 0 ? "#991B1B" : "#92400E" }}>
                          {label.charAt(0).toUpperCase() + label.slice(1)}
                          {s.days_overdue > 0 ? ` — overdue by ${s.days_overdue} days` : ""}
                        </p>
                      );
                    }
                    if (typeof s.days_idle === "number" && s.days_idle > 0)
                      return <p className="text-xs mt-2 text-amber-700">Inactive for {s.days_idle} days</p>;
                    return null;
                  })()}
                  <button onClick={() => go(nextTask.link)} data-testid="gwp-next-go"
                    className="mt-3 inline-flex items-center gap-1 px-3.5 py-2 text-white text-xs font-semibold rounded-lg"
                    style={{ background: ORANGE }}>
                    Go to this step <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : allDone ? (
                <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg p-5 text-center" data-testid="gwp-all-done">
                  <CheckCircle2 className="w-9 h-9 text-emerald-500 mx-auto" />
                  <p className="text-sm font-bold text-emerald-800 mt-2">All steps complete!</p>
                  <p className="text-xs text-emerald-600 mt-0.5">Your BCM program is fully set up.</p>
                </div>
              ) : null}

              {/* Section C + D — Sections accordion */}
              <div className="mt-6 space-y-2">
                {(data.sections || []).map((sec) => {
                  const Icon = SECTION_ICON[sec.id] || Settings;
                  const isExp = !!expanded[sec.id];
                  const tint = sec.completed === sec.total ? "#ECFDF5"
                    : sec.completed > 0 ? "#FFF7ED" : "#F1F5F9";
                  const tasksIn = (data.tasks || []).filter((t) => t.section === sec.id);
                  return (
                    <div key={sec.id} className="rounded-lg overflow-hidden border border-slate-200">
                      <button
                        onClick={() => setExpanded((p) => ({ ...p, [sec.id]: !p[sec.id] }))}
                        data-testid={`gwp-section-${sec.id}`}
                        style={{ background: tint }}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
                        <Icon className="w-4 h-4 text-slate-600" />
                        <span className="text-sm font-semibold text-slate-800 flex-1">{sec.label}</span>
                        <span className="text-xs font-bold text-slate-600 bg-white/70 rounded-full px-2 py-0.5">
                          {sec.completed} / {sec.total}
                        </span>
                      </button>
                      {isExp && (
                        <div className="bg-white divide-y divide-slate-100">
                          {tasksIn.map((t) => {
                            const badge = stallBadge(t.stall_info);
                            return (
                              <div key={t.id} onClick={() => go(t.link)}
                                data-testid={`gwp-task-${t.id}`}
                                className="flex items-start gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-slate-50"
                                style={t.is_next ? { background: "#FFF7ED" } : undefined}>
                                <span className="mt-0.5">{STATUS_ICON(t.status)}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-sm font-medium text-slate-800">{t.title}</span>
                                    {t.is_next && (
                                      <span className="text-[9px] font-bold text-white rounded px-1.5 py-0.5" style={{ background: ORANGE }}>NEXT</span>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-500 mt-0.5">{t.hint}</p>
                                  <div className="flex items-center gap-1.5 mt-1">
                                    {t.count != null && (
                                      <span className="text-[10px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{t.count} items</span>
                                    )}
                                    {badge && (
                                      <span className="text-[10px] font-semibold rounded px-1.5 py-0.5" style={toneStyle[badge.tone]}>{badge.text}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
